/**
=========================================================
* Soft UI Dashboard React - Profile (labels + searchable country)
=========================================================
*/
// /src/layouts/
// src/layouts/profile/index.js

import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

// MUI
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import CircularProgress from "@mui/material/CircularProgress";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PersonIcon from "@mui/icons-material/Person";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

// Soft UI
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

// API
import { profile as fetchProfileRaw } from "api/apiService";
import { updateUserProfile } from "api/apiService";

// Toasts
import { notifyCenter } from "utils/toast";

/* -------------------------------- helpers -------------------------------- */

// Bold, black label above inputs
const Label = ({ children }) => (
  <SoftTypography
    variant="caption"
    sx={{ fontWeight: 700, color: "#111", mb: 0.5, display: "block" }}
  >
    {children}
  </SoftTypography>
);
Label.propTypes = { children: PropTypes.node };

// 2-letter upper-case helper
const to2 = (v) => String(v ?? "").trim().slice(0, 2).toUpperCase();

/* --------------------------- design tokens (V2) ---------------------------
   Samme språk som bestillingslisten og ordresiden, så profilsiden hører til
   samme familie: kortradius 16, tynn linje, dus bakgrunn, merkeblå. */
const BRAND = "#006bb7";
const INK = "#111c2d";
const MUTED = "#67748e";
const LINE = "#e6ecf3";
const PAGE_BG = "#f4f7fb";

const CARD = {
  borderRadius: "16px",
  border: `1px solid ${LINE}`,
  boxShadow: "0 2px 14px rgba(16,24,40,.05)",
  height: "100%",
};

const ROLE_LABELS = {
  admin: "Administrator",
  admin_staff: "Administrasjon",
  customer: "Kunde",
  vendor: "Leverandør",
  vendor_staff: "Leverandør",
};

// Kortoverskrift med ikon, som på ordresiden.
const CardHead = ({ icon, title }) => (
  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: "10px",
        display: "grid",
        placeItems: "center",
        backgroundColor: "rgba(0,107,183,.09)",
        color: BRAND,
      }}
    >
      {icon}
    </Box>
    <SoftTypography sx={{ fontSize: 14.5, fontWeight: 700, color: INK }}>{title}</SoftTypography>
  </Stack>
);
CardHead.propTypes = { icon: PropTypes.node, title: PropTypes.string };
CardHead.defaultProps = { icon: null, title: "" };

// Etikett/verdi-rad i infokortene.
const Row = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <SoftTypography
      sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".4px" }}
    >
      {label}
    </SoftTypography>
    <SoftTypography sx={{ fontSize: 14, fontWeight: 500, color: INK }}>
      {value || "–"}
    </SoftTypography>
  </Box>
);
Row.propTypes = { label: PropTypes.string, value: PropTypes.node };
Row.defaultProps = { label: "", value: "" };

const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

// Én adresse (faktura eller levering) som radene i kortet.
const addressRows = (a = {}, prefix) => ({
  name: [a[`${prefix}_first_name`], a[`${prefix}_last_name`]].filter(Boolean).join(" "),
  company: a[`${prefix}_company`] || "",
  street: [a[`${prefix}_address_1`], a[`${prefix}_address_2`]].filter(Boolean).join(", "),
  place: [a[`${prefix}_postcode`], a[`${prefix}_city`]].filter(Boolean).join(" "),
  country: a[`${prefix}_country`] || "",
  phone: a[`${prefix}_phone`] || "",
  email: a[`${prefix}_email`] || "",
});

/* ------------------------------ country list ------------------------------ */
const COUNTRIES = [
  { code: "AD", name: "Andorra" }, { code: "AE", name: "United Arab Emirates" },
  { code: "AF", name: "Afghanistan" }, { code: "AG", name: "Antigua and Barbuda" },
  { code: "AI", name: "Anguilla" }, { code: "AL", name: "Albania" },
  { code: "AM", name: "Armenia" }, { code: "AO", name: "Angola" },
  { code: "AQ", name: "Antarctica" }, { code: "AR", name: "Argentina" },
  { code: "AS", name: "American Samoa" }, { code: "AT", name: "Austria" },
  { code: "AU", name: "Australia" }, { code: "AW", name: "Aruba" },
  { code: "AX", name: "Åland Islands" }, { code: "AZ", name: "Azerbaijan" },
  { code: "BA", name: "Bosnia and Herzegovina" }, { code: "BB", name: "Barbados" },
  { code: "BD", name: "Bangladesh" }, { code: "BE", name: "Belgium" },
  { code: "BF", name: "Burkina Faso" }, { code: "BG", name: "Bulgaria" },
  { code: "BH", name: "Bahrain" }, { code: "BI", name: "Burundi" },
  { code: "BJ", name: "Benin" }, { code: "BL", name: "Saint Barthélemy" },
  { code: "BM", name: "Bermuda" }, { code: "BN", name: "Brunei Darussalam" },
  { code: "BO", name: "Bolivia (Plurinational State of)" },
  { code: "BQ", name: "Bonaire, Sint Eustatius and Saba" },
  { code: "BR", name: "Brazil" }, { code: "BS", name: "Bahamas" },
  { code: "BT", name: "Bhutan" }, { code: "BV", name: "Bouvet Island" },
  { code: "BW", name: "Botswana" }, { code: "BY", name: "Belarus" },
  { code: "BZ", name: "Belize" }, { code: "CA", name: "Canada" },
  { code: "CC", name: "Cocos (Keeling) Islands" },
  { code: "CD", name: "Congo, Democratic Republic of the" },
  { code: "CF", name: "Central African Republic" }, { code: "CG", name: "Congo" },
  { code: "CH", name: "Switzerland" }, { code: "CI", name: "Côte d'Ivoire" },
  { code: "CK", name: "Cook Islands" }, { code: "CL", name: "Chile" },
  { code: "CM", name: "Cameroon" }, { code: "CN", name: "China" },
  { code: "CO", name: "Colombia" }, { code: "CR", name: "Costa Rica" },
  { code: "CU", name: "Cuba" }, { code: "CV", name: "Cabo Verde" },
  { code: "CW", name: "Curaçao" }, { code: "CX", name: "Christmas Island" },
  { code: "CY", name: "Cyprus" }, { code: "CZ", name: "Czechia" },
  { code: "DE", name: "Germany" }, { code: "DJ", name: "Djibouti" },
  { code: "DK", name: "Denmark" }, { code: "DM", name: "Dominica" },
  { code: "DO", name: "Dominican Republic" }, { code: "DZ", name: "Algeria" },
  { code: "EC", name: "Ecuador" }, { code: "EE", name: "Estonia" },
  { code: "EG", name: "Egypt" }, { code: "EH", name: "Western Sahara" },
  { code: "ER", name: "Eritrea" }, { code: "ES", name: "Spain" },
  { code: "ET", name: "Ethiopia" }, { code: "FI", name: "Finland" },
  { code: "FJ", name: "Fiji" }, { code: "FK", name: "Falkland Islands (Malvinas)" },
  { code: "FM", name: "Micronesia (Federated States of)" },
  { code: "FO", name: "Faroe Islands" }, { code: "FR", name: "France" },
  { code: "GA", name: "Gabon" }, { code: "GB", name: "United Kingdom of Great Britain and Northern Ireland" },
  { code: "GD", name: "Grenada" }, { code: "GE", name: "Georgia" },
  { code: "GF", name: "French Guiana" }, { code: "GG", name: "Guernsey" },
  { code: "GH", name: "Ghana" }, { code: "GI", name: "Gibraltar" },
  { code: "GL", name: "Greenland" }, { code: "GM", name: "Gambia" },
  { code: "GN", name: "Guinea" }, { code: "GP", name: "Guadeloupe" },
  { code: "GQ", name: "Equatorial Guinea" }, { code: "GR", name: "Greece" },
  { code: "GS", name: "South Georgia and the South Sandwich Islands" },
  { code: "GT", name: "Guatemala" }, { code: "GU", name: "Guam" },
  { code: "GW", name: "Guinea-Bissau" }, { code: "GY", name: "Guyana" },
  { code: "HK", name: "Hong Kong" }, { code: "HM", name: "Heard Island and McDonald Islands" },
  { code: "HN", name: "Honduras" }, { code: "HR", name: "Croatia" },
  { code: "HT", name: "Haiti" }, { code: "HU", name: "Hungary" },
  { code: "ID", name: "Indonesia" }, { code: "IE", name: "Ireland" },
  { code: "IM", name: "Isle of Man" }, { code: "IN", name: "India" },
  { code: "IO", name: "British Indian Ocean Territory" }, { code: "IQ", name: "Iraq" },
  { code: "IR", name: "Iran (Islamic Republic of)" }, { code: "IS", name: "Iceland" },
  { code: "IT", name: "Italy" }, { code: "JE", name: "Jersey" },
  { code: "JM", name: "Jamaica" }, { code: "JO", name: "Jordan" },
  { code: "JP", name: "Japan" }, { code: "KE", name: "Kenya" },
  { code: "KG", name: "Kyrgyzstan" }, { code: "KH", name: "Cambodia" },
  { code: "KI", name: "Kiribati" }, { code: "KM", name: "Comoros" },
  { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "KP", name: "Korea (Democratic People's Republic of)" },
  { code: "KR", name: "Korea, Republic of" }, { code: "KW", name: "Kuwait" },
  { code: "KY", name: "Cayman Islands" }, { code: "KZ", name: "Kazakhstan" },
  { code: "LA", name: "Lao People's Democratic Republic" }, { code: "LB", name: "Lebanon" },
  { code: "LC", name: "Saint Lucia" }, { code: "LI", name: "Liechtenstein" },
  { code: "LK", name: "Sri Lanka" }, { code: "LR", name: "Liberia" },
  { code: "LS", name: "Lesotho" }, { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" }, { code: "LV", name: "Latvia" },
  { code: "LY", name: "Libya" }, { code: "MA", name: "Morocco" },
  { code: "MC", name: "Monaco" }, { code: "MD", name: "Moldova, Republic of" },
  { code: "ME", name: "Montenegro" }, { code: "MF", name: "Saint Martin (French part)" },
  { code: "MG", name: "Madagascar" }, { code: "MH", name: "Marshall Islands" },
  { code: "MK", name: "North Macedonia" }, { code: "ML", name: "Mali" },
  { code: "MM", name: "Myanmar" }, { code: "MN", name: "Mongolia" },
  { code: "MO", name: "Macao" }, { code: "MP", name: "Northern Mariana Islands" },
  { code: "MQ", name: "Martinique" }, { code: "MR", name: "Mauritania" },
  { code: "MS", name: "Montserrat" }, { code: "MT", name: "Malta" },
  { code: "MU", name: "Mauritius" }, { code: "MV", name: "Maldives" },
  { code: "MW", name: "Malawi" }, { code: "MX", name: "Mexico" },
  { code: "MY", name: "Malaysia" }, { code: "MZ", name: "Mozambique" },
  { code: "NA", name: "Namibia" }, { code: "NC", name: "New Caledonia" },
  { code: "NE", name: "Niger" }, { code: "NF", name: "Norfolk Island" },
  { code: "NG", name: "Nigeria" }, { code: "NI", name: "Nicaragua" },
  { code: "NL", name: "Netherlands" }, { code: "NO", name: "Norway" },
  { code: "NP", name: "Nepal" }, { code: "NR", name: "Nauru" },
  { code: "NU", name: "Niue" }, { code: "NZ", name: "New Zealand" },
  { code: "OM", name: "Oman" }, { code: "PA", name: "Panama" },
  { code: "PE", name: "Peru" }, { code: "PF", name: "French Polynesia" },
  { code: "PG", name: "Papua New Guinea" }, { code: "PH", name: "Philippines" },
  { code: "PK", name: "Pakistan" }, { code: "PL", name: "Poland" },
  { code: "PM", name: "Saint Pierre and Miquelon" }, { code: "PN", name: "Pitcairn" },
  { code: "PR", name: "Puerto Rico" }, { code: "PS", name: "Palestine, State of" },
  { code: "PT", name: "Portugal" }, { code: "PW", name: "Palau" },
  { code: "PY", name: "Paraguay" }, { code: "QA", name: "Qatar" },
  { code: "RE", name: "Réunion" }, { code: "RO", name: "Romania" },
  { code: "RS", name: "Serbia" }, { code: "RU", name: "Russian Federation" },
  { code: "RW", name: "Rwanda" }, { code: "SA", name: "Saudi Arabia" },
  { code: "SB", name: "Solomon Islands" }, { code: "SC", name: "Seychelles" },
  { code: "SD", name: "Sudan" }, { code: "SE", name: "Sweden" },
  { code: "SG", name: "Singapore" }, { code: "SH", name: "Saint Helena, Ascension and Tristan da Cunha" },
  { code: "SI", name: "Slovenia" }, { code: "SJ", name: "Svalbard and Jan Mayen" },
  { code: "SK", name: "Slovakia" }, { code: "SL", name: "Sierra Leone" },
  { code: "SM", name: "San Marino" }, { code: "SN", name: "Senegal" },
  { code: "SO", name: "Somalia" }, { code: "SR", name: "Suriname" },
  { code: "SS", name: "South Sudan" }, { code: "ST", name: "Sao Tome and Principe" },
  { code: "SV", name: "El Salvador" }, { code: "SX", name: "Sint Maarten (Dutch part)" },
  { code: "SY", name: "Syrian Arab Republic" }, { code: "SZ", name: "Eswatini" },
  { code: "TC", name: "Turks and Caicos Islands" }, { code: "TD", name: "Chad" },
  { code: "TF", name: "French Southern Territories" }, { code: "TG", name: "Togo" },
  { code: "TH", name: "Thailand" }, { code: "TJ", name: "Tajikistan" },
  { code: "TK", name: "Tokelau" }, { code: "TL", name: "Timor-Leste" },
  { code: "TM", name: "Turkmenistan" }, { code: "TN", name: "Tunisia" },
  { code: "TO", name: "Tonga" }, { code: "TR", name: "Türkiye" },
  { code: "TT", name: "Trinidad and Tobago" }, { code: "TV", name: "Tuvalu" },
  { code: "TW", name: "Taiwan, Province of China" }, { code: "TZ", name: "Tanzania, United Republic of" },
  { code: "UA", name: "Ukraine" }, { code: "UG", name: "Uganda" },
  { code: "UM", name: "United States Minor Outlying Islands" },
  { code: "US", name: "United States of America" }, { code: "UY", name: "Uruguay" },
  { code: "UZ", name: "Uzbekistan" }, { code: "VA", name: "Holy See" },
  { code: "VC", name: "Saint Vincent and the Grenadines" },
  { code: "VE", name: "Venezuela (Bolivarian Republic of)" },
  { code: "VG", name: "Virgin Islands (British)" },
  { code: "VI", name: "Virgin Islands (U.S.)" }, { code: "VN", name: "Viet Nam" },
  { code: "VU", name: "Vanuatu" }, { code: "WF", name: "Wallis and Futuna" },
  { code: "WS", name: "Samoa" }, { code: "YE", name: "Yemen" },
  { code: "YT", name: "Mayotte" }, { code: "ZA", name: "South Africa" },
  { code: "ZM", name: "Zambia" }, { code: "ZW", name: "Zimbabwe" },
  // extras
  { code: "XK", name: "Kosovo" }, { code: "UK", name: "United Kingdom" },
  { code: "EU", name: "European Union" }, { code: "AC", name: "Ascension Island" },
  { code: "TA", name: "Tristan da Cunha" }, { code: "IC", name: "Canary Islands" },
  { code: "EA", name: "Ceuta and Melilla" },
];

/* --------------------------- small skeleton set --------------------------- */
const FieldSkeleton = () => <Skeleton variant="rounded" height={56} />;
const SavingSkeleton = () => (
  <Grid container spacing={2} sx={{ pt: 0.5 }}>
    {Array.from({ length: 18 }).map((_, i) => (
      <Grid key={i} item xs={12} sm={6}>
        <FieldSkeleton />
      </Grid>
    ))}
  </Grid>
);

/* -------------------------------- component ------------------------------- */

const lsUser = JSON.parse(localStorage.getItem("user") || "null");
const userId = lsUser?.id || lsUser?.ID || lsUser?.userId || lsUser?.data?.ID;

export default function Overview() {
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // drives card + modal skeletons on fetch/save

  // header state
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");

  // modal/editing state
  const [openEdit, setOpenEdit] = useState(false);
  const [saving, setSaving] = useState(false); // disables inputs + shows dialog skeleton while saving
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});

  /* ------------------------------ load profile ------------------------------ */
  useEffect(() => {
    setIsLoading(true);
    const currentRole = localStorage.getItem("role");
    if (lsUser) {
      setFullName(lsUser.fullName || lsUser.display_name || "");
      setRole(currentRole || "");
    }

    (async () => {
      try {
        const res = await fetchProfileRaw("profile", {}, userId);
        if (res?.status) {
          const data = res.data_info;
          setProfileData(data);

          const display =
            `${data?.first_name || ""} ${data?.last_name || ""}`.trim() ||
            data?.display_name ||
            "";
          if (display) setFullName(display);
        }
      } catch (e) {
        console.error("Profile fetch failed:", e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  /* --------------------------- resolved view fields -------------------------- */
  const name =
    `${profileData?.first_name || ""} ${profileData?.last_name || ""}`.trim() ||
    profileData?.display_name ||
    fullName;

  const email =
    profileData?.email ||
    profileData?.billing?.billing_email ||
    profileData?.shipping?.shipping_email ||
    "";

  const phone =
    profileData?.phoneNumber ||
    profileData?.billing?.billing_phone ||
    profileData?.shipping?.shipping_phone ||
    "";

  /* ---------------------- create form from profile data --------------------- */
  const buildFormFromProfile = (p) => ({
    first_name: p?.first_name || "",
    last_name: p?.last_name || "",

    billing_first_name: p?.billing?.billing_first_name || "",
    billing_last_name: p?.billing?.billing_last_name || "",
    billing_company: p?.billing?.billing_company || "",
    billing_address_1: p?.billing?.billing_address_1 || "",
    billing_address_2: p?.billing?.billing_address_2 ?? "",
    billing_city: p?.billing?.billing_city || "",
    billing_state: p?.billing?.billing_state || "",
    billing_postcode: p?.billing?.billing_postcode || "",
    billing_country: to2(p?.billing?.billing_country || "NO"),
    billing_email: p?.billing?.billing_email || p?.email || "",
    billing_phone: p?.billing?.billing_phone || "",

    shipping_first_name: p?.shipping?.shipping_first_name || "",
    shipping_last_name: p?.shipping?.shipping_last_name || "",
    shipping_company: p?.shipping?.shipping_company || "",
    shipping_address_1: p?.shipping?.shipping_address_1 || "",
    shipping_address_2: p?.shipping?.shipping_address_2 ?? "",
    shipping_city: p?.shipping?.shipping_city || "",
    shipping_state: p?.shipping?.shipping_state || "",
    shipping_postcode: p?.shipping?.shipping_postcode || "",
    shipping_country: to2(p?.shipping?.shipping_country || "NO"),
    shipping_email: p?.shipping?.shipping_email || "",
    shipping_phone: p?.shipping?.shipping_phone || "",
  });

  /* --------------------------- open/close edit modal ------------------------ */
  const openEditModal = () => {
    setErrors({});
    setForm(buildFormFromProfile(profileData || {}));
    setOpenEdit(true);
  };
  const closeEditModal = () => {
    if (!saving) setOpenEdit(false);
  };

  /* -------------------------------- validate -------------------------------- */
  const requiredKeys = useMemo(
    () =>
      Object.keys(buildFormFromProfile({})).filter(
        (k) => !["billing_address_2", "shipping_address_2"].includes(k)
      ),
    []
  );

  const validate = (draft) => {
    const e = {};
    requiredKeys.forEach((k) => {
      if (!String(draft[k] ?? "").trim()) e[k] = "Påkrevd felt";
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ------------------------------- handlers -------------------------------- */
  const commonInputSx = {
    padding: "8px 14px !important",
    "& input": { width: "calc(100% - 0px) !important" },
  };

  const handleChange = (field) => (ev) => {
    const v = ev.target.value;
    setForm((f) => ({ ...f, [field]: v }));
  };

  const handleSave = async () => {
    if (!form) return;
    if (!validate(form)) {
      notifyCenter("warning", "Vennligst fyll ut alle påkrevde felter.", { duration: 2500 });
      return;
    }

    try {
      // show skeletons in both the card and the dialog
      setIsLoading(true);
      setSaving(true);

      // force 2-letter country codes
      const payload = {
        ...form,
        billing_country: to2(form.billing_country),
        shipping_country: to2(form.shipping_country),
      };

      const res = await updateUserProfile("update_user", payload, userId);
      if (res?.status) {
        setOpenEdit(false);
        notifyCenter("success", "Profilen ble oppdatert!", { duration: 2000 });

        // refresh profile
        const fresh = await fetchProfileRaw("profile", {}, userId);
        if (fresh?.status) {
          const data = fresh.data_info;
          setProfileData(data);
          const display =
            `${data?.first_name || ""} ${data?.last_name || ""}`.trim() ||
            data?.display_name ||
            "";
          if (display) setFullName(display);
        }
        
      } else {
        notifyCenter("error", "Kunne ikke oppdatere profilen.", { duration: 2500 });
      }
    } catch (err) {
      console.error(err);
      notifyCenter("error", "En feil oppstod under lagring.", { duration: 2500 });
    } finally {
      setSaving(false);
      setIsLoading(false);
    }
  };

  /* --------------------------------- render -------------------------------- */
  const billing = addressRows(profileData?.billing || {}, "billing");
  const shipping = addressRows(profileData?.shipping || {}, "shipping");

  return (
    <DashboardLayout>
      <SoftBox sx={{ backgroundColor: PAGE_BG, mx: -3, px: 3, pt: 2, pb: 6, minHeight: "100vh" }}>
        {/* ------------------------------- hero -------------------------------- */}
        <Card sx={{ ...CARD, mb: 3, height: "auto", overflow: "hidden" }}>
          <SoftBox
            sx={{
              p: { xs: 3, md: 4 },
              background: "linear-gradient(120deg, #0b2a4a 0%, #0f3e6e 55%, #006bb7 100%)",
            }}
          >
            {isLoading ? (
              <Skeleton variant="rectangular" height={96} sx={{ borderRadius: 2, bgcolor: "rgba(255,255,255,.15)" }} />
            ) : (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={2.5}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    fontSize: 26,
                    fontWeight: 800,
                    color: "#fff",
                    backgroundColor: "rgba(255,255,255,.14)",
                    border: "2px solid rgba(255,255,255,.35)",
                  }}
                >
                  {initialsOf(name)}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap">
                    <SoftTypography sx={{ fontSize: 24, fontWeight: 800, color: "#fff" }}>
                      {name || "—"}
                    </SoftTypography>
                    <Chip
                      size="small"
                      label={ROLE_LABELS[role] || role || "Bruker"}
                      sx={{
                        backgroundColor: "rgba(255,255,255,.16)",
                        color: "#fff",
                        fontWeight: 700,
                        height: 22,
                      }}
                    />
                  </Stack>
                  {profileData?.companyName && (
                    <SoftTypography sx={{ fontSize: 13.5, color: "rgba(255,255,255,.85)", mt: 0.25 }}>
                      {profileData.companyName}
                    </SoftTypography>
                  )}
                  <SoftTypography sx={{ fontSize: 13, color: "rgba(255,255,255,.75)", mt: 0.25 }}>
                    {email}
                  </SoftTypography>
                </Box>

                <SoftButton
                  onClick={openEditModal}
                  sx={{
                    backgroundColor: "#fff",
                    color: BRAND,
                    borderRadius: "12px",
                    flexShrink: 0,
                    "&:hover": { backgroundColor: "#eef5fb" },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 17, mr: 0.75 }} />
                  Rediger profil
                </SoftButton>
              </Stack>
            )}
          </SoftBox>
        </Card>

        {/* --------------------------- info cards ------------------------------ */}
        {isLoading ? (
          <Grid container spacing={2}>
            {[0, 1, 2].map((i) => (
              <Grid key={i} item xs={12} md={4}>
                <Skeleton variant="rounded" height={220} sx={{ borderRadius: "16px" }} />
              </Grid>
            ))}
          </Grid>
        ) : profileData ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card sx={CARD}>
                <SoftBox p={3}>
                  <CardHead icon={<PersonIcon sx={{ fontSize: 18 }} />} title="Kontaktinformasjon" />
                  <Row label="Navn" value={name} />
                  <Row
                    label="E-post"
                    value={
                      email && (
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <EmailIcon sx={{ fontSize: 15, color: MUTED }} />
                          <span>{email}</span>
                        </Stack>
                      )
                    }
                  />
                  <Row
                    label="Telefon"
                    value={
                      phone && (
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <PhoneIcon sx={{ fontSize: 15, color: MUTED }} />
                          <span>{phone}</span>
                        </Stack>
                      )
                    }
                  />
                  {profileData?.companyName && (
                    <Row
                      label="Firma"
                      value={
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <BusinessIcon sx={{ fontSize: 15, color: MUTED }} />
                          <span>{profileData.companyName}</span>
                        </Stack>
                      }
                    />
                  )}
                </SoftBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={CARD}>
                <SoftBox p={3}>
                  <CardHead icon={<ReceiptLongIcon sx={{ fontSize: 18 }} />} title="Fakturaadresse" />
                  <Row label="Navn" value={billing.name} />
                  {billing.company && <Row label="Firma" value={billing.company} />}
                  <Row label="Adresse" value={billing.street} />
                  <Row label="Poststed" value={billing.place} />
                  <Row label="Land" value={billing.country} />
                </SoftBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={CARD}>
                <SoftBox p={3}>
                  <CardHead icon={<LocalShippingIcon sx={{ fontSize: 18 }} />} title="Leveringsadresse" />
                  <Row label="Navn" value={shipping.name} />
                  {shipping.company && <Row label="Firma" value={shipping.company} />}
                  <Row label="Adresse" value={shipping.street} />
                  <Row label="Poststed" value={shipping.place} />
                  <Row label="Land" value={shipping.country} />
                </SoftBox>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Card sx={{ ...CARD, height: "auto" }}>
            <SoftBox p={3}>
              <SoftTypography sx={{ fontSize: 14, color: MUTED }}>
                Fant ingen profildata.
              </SoftTypography>
            </SoftBox>
          </Card>
        )}
      </SoftBox>

      {/* Edit modal */}
      <Dialog open={openEdit} onClose={closeEditModal} maxWidth="md" fullWidth keepMounted>
        <DialogTitle>
          Rediger profil {saving && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </DialogTitle>

        <DialogContent dividers>
          {/* While saving, show a full skeleton in the dialog */}
          {saving ? (
            <SavingSkeleton />
          ) : form ? (
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              {/* Basic */}
              <Grid item xs={12} sm={6}>
                <Label>Fornavn</Label>
                <TextField
                  placeholder="Fornavn"
                  value={form.first_name}
                  onChange={handleChange("first_name")}
                  error={!!errors.first_name}
                  helperText={errors.first_name}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Etternavn</Label>
                <TextField
                  placeholder="Etternavn"
                  value={form.last_name}
                  onChange={handleChange("last_name")}
                  error={!!errors.last_name}
                  helperText={errors.last_name}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>

              {/* Billing */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <SoftTypography variant="button" color="text">
                  Fakturaadresse
                </SoftTypography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Label>Fornavn</Label>
                <TextField
                  placeholder="Fornavn"
                  value={form.billing_first_name}
                  onChange={handleChange("billing_first_name")}
                  error={!!errors.billing_first_name}
                  helperText={errors.billing_first_name}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Etternavn</Label>
                <TextField
                  placeholder="Etternavn"
                  value={form.billing_last_name}
                  onChange={handleChange("billing_last_name")}
                  error={!!errors.billing_last_name}
                  helperText={errors.billing_last_name}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Firma</Label>
                <TextField
                  placeholder="Firma"
                  value={form.billing_company}
                  onChange={handleChange("billing_company")}
                  error={!!errors.billing_company}
                  helperText={errors.billing_company}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Adresse 1</Label>
                <TextField
                  placeholder="Adresse 1"
                  value={form.billing_address_1}
                  onChange={handleChange("billing_address_1")}
                  error={!!errors.billing_address_1}
                  helperText={errors.billing_address_1}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Adresse 2</Label>
                <TextField
                  placeholder="Adresse 2"
                  value={form.billing_address_2}
                  onChange={handleChange("billing_address_2")}
                  error={!!errors.billing_address_2}
                  helperText={errors.billing_address_2}
                  fullWidth
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>By</Label>
                <TextField
                  placeholder="By"
                  value={form.billing_city}
                  onChange={handleChange("billing_city")}
                  error={!!errors.billing_city}
                  helperText={errors.billing_city}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Stat</Label>
                <TextField
                  placeholder="Stat"
                  value={form.billing_state}
                  onChange={handleChange("billing_state")}
                  error={!!errors.billing_state}
                  helperText={errors.billing_state}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Postnummer</Label>
                <TextField
                  placeholder="Postnummer"
                  value={form.billing_postcode}
                  onChange={handleChange("billing_postcode")}
                  error={!!errors.billing_postcode}
                  helperText={errors.billing_postcode}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Label>Land</Label>
                <Autocomplete
                  options={COUNTRIES}
                  autoHighlight
                  getOptionLabel={(o) => (o ? o.name : "")}
                  isOptionEqualToValue={(o, v) => o.code === v.code}
                  value={COUNTRIES.find((c) => c.code === form.billing_country) || null}
                  onChange={(_, val) =>
                    setForm((f) => ({ ...f, billing_country: val ? val.code : "" }))
                  }
                  disabled={saving}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Land"
                      error={!!errors.billing_country}
                      helperText={errors.billing_country}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Label>E-post</Label>
                <TextField
                  placeholder="E-post"
                  value={form.billing_email}
                  onChange={handleChange("billing_email")}
                  error={!!errors.billing_email}
                  helperText={errors.billing_email}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Telefonnummer</Label>
                <TextField
                  placeholder="Telefonnummer"
                  value={form.billing_phone}
                  onChange={handleChange("billing_phone")}
                  error={!!errors.billing_phone}
                  helperText={errors.billing_phone}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>

              {/* Shipping */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <SoftTypography variant="button" color="text">
                  Leveringsadresse
                </SoftTypography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Label>Fornavn</Label>
                <TextField
                  placeholder="Fornavn"
                  value={form.shipping_first_name}
                  onChange={handleChange("shipping_first_name")}
                  error={!!errors.shipping_first_name}
                  helperText={errors.shipping_first_name}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Etternavn</Label>
                <TextField
                  placeholder="Etternavn"
                  value={form.shipping_last_name}
                  onChange={handleChange("shipping_last_name")}
                  error={!!errors.shipping_last_name}
                  helperText={errors.shipping_last_name}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Firma</Label>
                <TextField
                  placeholder="Firma"
                  value={form.shipping_company}
                  onChange={handleChange("shipping_company")}
                  error={!!errors.shipping_company}
                  helperText={errors.shipping_company}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Adresse 1</Label>
                <TextField
                  placeholder="Adresse 1"
                  value={form.shipping_address_1}
                  onChange={handleChange("shipping_address_1")}
                  error={!!errors.shipping_address_1}
                  helperText={errors.shipping_address_1}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Adresse 2</Label>
                <TextField
                  placeholder="Adresse 2"
                  value={form.shipping_address_2}
                  onChange={handleChange("shipping_address_2")}
                  error={!!errors.shipping_address_2}
                  helperText={errors.shipping_address_2}
                  fullWidth
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>By</Label>
                <TextField
                  placeholder="By"
                  value={form.shipping_city}
                  onChange={handleChange("shipping_city")}
                  error={!!errors.shipping_city}
                  helperText={errors.shipping_city}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Stat</Label>
                <TextField
                  placeholder="Stat"
                  value={form.shipping_state}
                  onChange={handleChange("shipping_state")}
                  error={!!errors.shipping_state}
                  helperText={errors.shipping_state}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Postnummer</Label>
                <TextField
                  placeholder="Postnummer"
                  value={form.shipping_postcode}
                  onChange={handleChange("shipping_postcode")}
                  error={!!errors.shipping_postcode}
                  helperText={errors.shipping_postcode}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Label>Land</Label>
                <Autocomplete
                  options={COUNTRIES}
                  autoHighlight
                  getOptionLabel={(o) => (o ? o.name : "")}
                  isOptionEqualToValue={(o, v) => o.code === v.code}
                  value={COUNTRIES.find((c) => c.code === form.shipping_country) || null}
                  onChange={(_, val) =>
                    setForm((f) => ({ ...f, shipping_country: val ? val.code : "" }))
                  }
                  disabled={saving}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Land"
                      error={!!errors.shipping_country}
                      helperText={errors.shipping_country}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Label>E-post</Label>
                <TextField
                  placeholder="E-post"
                  value={form.shipping_email}
                  onChange={handleChange("shipping_email")}
                  error={!!errors.shipping_email}
                  helperText={errors.shipping_email}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Telefonnummer</Label>
                <TextField
                  placeholder="Telefonnummer"
                  value={form.shipping_phone}
                  onChange={handleChange("shipping_phone")}
                  error={!!errors.shipping_phone}
                  helperText={errors.shipping_phone}
                  fullWidth
                  required
                  disabled={saving}
                  InputProps={{ sx: commonInputSx }}
                />
              </Grid>
            </Grid>
          ) : (
            <Skeleton variant="rectangular" height={200} />
          )}
        </DialogContent>

        <DialogActions>
          <SoftButton
            color="secondary"
            variant="outlined"
            onClick={closeEditModal}
            disabled={saving}
          >
            Avbryt
          </SoftButton>
          <SoftButton color="info" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            {saving ? "Lagrer…" : "Lagre"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
